import React, { useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/PopularTests.css";
import imgCBC from "../assets/Cbc.jpg";
import imgThyroid from "../assets/Thyroid Profile.jpg";
import imgLipid from "../assets/Lipid Profile.jpg";
import imgVitaminD from "../assets/Vitamin D.jpg";
import imgHbA1c from "../assets/HbA1c.png";
import imgPSA from "../assets/PSA.png";

/* ─────────────────────────────────────────────
   WhatsApp number — same as Navbar contact number
   (73587 98182) prefixed with India country code
───────────────────────────────────────────── */
const WHATSAPP_NUMBER = "917358798182";

/* Route + section id that "View All Tests" should land on */
const VIEW_ALL_ROUTE = "/service";
const VIEW_ALL_TARGET_ID = "diagnosticservices";

function scrollToId(id, retries = 10) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else if (retries > 0) {
    setTimeout(() => scrollToId(id, retries - 1), 80);
  }
}

function handleBookNow(test) {
  const message = `Hi, I'm interested in booking the *${test.name}* (${test.subtitle}). Please share the available slots.`;
  const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    message
  )}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

const tests = [
  {
    img: imgCBC,
    name: "CBC",
    subtitle: "Complete Blood Count",
    sample: "Blood",
    reportTime: "24 hrs",
    hoverDetail:
      "Measures RBC, WBC, Platelets and Haemoglobin levels to screen for anaemia, infection and more.",
  },
  {
    img: imgHbA1c,
    name: "HbA1c",
    subtitle: "Glycosylated Haemoglobin",
    sample: "Blood",
    reportTime: "24 hrs",
    hoverDetail:
      "Reflects average blood glucose over 2–3 months — key test for diabetes diagnosis and monitoring.",
  },
  {
    img: imgThyroid,
    name: "Thyroid Profile",
    subtitle: "T3 · T4 · TSH",
    sample: "Blood",
    reportTime: "24 hrs",
    hoverDetail:
      "Evaluates thyroid gland function — detects hypothyroidism, hyperthyroidism and related disorders.",
  },
  {
    img: imgLipid,
    name: "Lipid Profile",
    subtitle: "Cholesterol · HDL · LDL · TG",
    sample: "Serum",
    reportTime: "24 hrs",
    hoverDetail:
      "Assesses cardiovascular risk by measuring total cholesterol, good & bad fats, and triglycerides.",
  },
  {
    img: imgVitaminD,
    name: "Vitamin D (25 OH)",
    subtitle: "Vitamin D Status",
    sample: "Blood",
    reportTime: "24 hrs",
    hoverDetail:
      "Detects Vitamin D deficiency linked to bone health, immunity, muscle function and mood.",
  },
  {
    img: imgPSA,
    name: "PSA Test",
    subtitle: "Prostate Specific Antigen",
    sample: "Blood",
    reportTime: "24 hrs",
    hoverDetail:
      "Screens for prostate issues including benign enlargement, inflammation and prostate cancer.",
  },
];

/* ── Card component ── */
const SlideCard = ({ test, onBook }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="test-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* ── FRONT ── */}
      <div className={`card-face card-front ${hovered ? "face-hidden" : ""}`}>
        {/* Image banner */}
        <div className="card-img-banner">
          <img src={test.img} alt={test.name} loading="lazy" className="card-banner-img" />
          <div className="banner-overlay" />
        </div>

        {/* Body */}
        <div className="card-body">
          <div className="test-name">{test.name}</div>
          <div className="test-subtitle">{test.subtitle}</div>

          {/* Sample + Report as pills */}
          <div className="test-meta">
            <div className="meta-pill">
              <i className="fa-solid fa-droplet meta-pill-icon"></i>
              <span className="meta-pill-label">Sample:</span>
              <span className="meta-pill-value">{test.sample}</span>
            </div>
            <div className="meta-pill">
              <i className="fa-regular fa-clock meta-pill-icon"></i>
              <span className="meta-pill-label">Report:</span>
              <span className="meta-pill-value">{test.reportTime}</span>
            </div>
          </div>

          <div className="card-footer">
            <button className="book-btn" onClick={() => onBook(test)}>
              <i className="fa-brands fa-whatsapp me-2"></i>
              Book Test
            </button>
          </div>
        </div>
      </div>

      {/* ── BACK — single navy gradient, description + hover book button ── */}
      <div className={`card-face card-back ${hovered ? "face-visible" : ""}`}>
        <div className="back-test-name">{test.name}</div>
        <div className="back-test-sub">{test.subtitle}</div>
        <div className="back-divider" />
        <p className="back-detail">{test.hoverDetail}</p>
        <button className="back-book-btn" onClick={() => onBook(test)}>
          <i className="fa-brands fa-whatsapp me-2"></i>
          Book Now
        </button>
      </div>
    </div>
  );
};

/* ── Main section ── */
const PopularTests = () => {
  const sliderRef = useRef(null);
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const getCardsPerView = () => {
    if (window.innerWidth <= 576) return 1;
    if (window.innerWidth <= 768) return 2;
    return 3;
  };

  const scroll = (dir) => {
    const slider = sliderRef.current;
    if (!slider) return;
    const card = slider.querySelector(".card-animate-wrapper");
    if (!card) return;
    const gap = parseInt(window.getComputedStyle(slider).columnGap || 24);
    const dist = (card.offsetWidth + gap) * getCardsPerView();
    slider.scrollBy({ left: dir * dist, behavior: "smooth" });
  };

  // function handleViewAllTests() {
  //   if (location.pathname === VIEW_ALL_ROUTE) {
  //     scrollToId(VIEW_ALL_TARGET_ID);
  //   } else {
  //     navigate(VIEW_ALL_ROUTE);
  //     setTimeout(() => scrollToId(VIEW_ALL_TARGET_ID), 120);
  //   }
  // }

  return (
    <section className="popular-tests-section" ref={sectionRef}>
      <div className={`popular-tests-header ${visible ? "pt-header-visible" : ""}`}>
        <span className="popular-tests-subtitle pt-animate-up pt-delay-0">POPULAR TESTS</span>
        <h2 className="popular-tests-title pt-animate-up pt-delay-1">
          Most Booked Diagnostic Tests
        </h2>
        <p className="popular-tests-description pt-animate-up pt-delay-2">
          Trusted diagnostic tests performed with precision, accuracy and quick
          turnaround times to support better healthcare decisions.
        </p>
      </div>

      <div className="carousel-container">
        <button className="carousel-arrow left" onClick={() => scroll(-1)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="popular-tests-slider" ref={sliderRef}>
          {tests.map((test, i) => (
            <div
              key={i}
              className={`card-animate-wrapper ${visible ? "card-visible" : ""}`}
              style={{ animationDelay: `${0.08 + i * 0.1}s` }}
            >
              <SlideCard test={test} onBook={handleBookNow} />
            </div>
          ))}
        </div>

        <button className="carousel-arrow right" onClick={() => scroll(1)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* <div
        className={`view-all-wrapper pt-anim-up ${visible ? "pt-anim-in" : ""}`}
        style={{ transitionDelay: "0.45s" }}
      >
        <button className="view-all-btn" onClick={handleViewAllTests}>
          View All Tests <i className="fa-solid fa-arrow-right ms-2"></i>
        </button>
      </div> */}
    </section>
  );
};

export default PopularTests;